import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { MemberSelectorListItem, ScheduleDraftInput } from '../appService';

export interface ScheduleDraftStoreValue {
  readonly createDraft: {
    readonly selectedDate: string;
    readonly draft: ScheduleDraftInput;
  } | null;
  readonly editDrafts: ReadonlyMap<string, ScheduleDraftInput>;
  readonly setCreateDraft: (selectedDate: string, draft: ScheduleDraftInput) => void;
  readonly clearCreateDraft: () => void;
  readonly setEditDraft: (scheduleId: string, draft: ScheduleDraftInput) => void;
  readonly clearEditDraft: (scheduleId: string) => void;
}

export interface MemberGroupCreateDraftStoreValue {
  readonly name: string;
  readonly initialMember: MemberSelectorListItem | null;
  readonly setName: (name: string) => void;
  readonly setInitialMember: (member: MemberSelectorListItem | null) => void;
  readonly clearDraft: () => void;
}

const ScheduleDraftStoreContext = createContext<ScheduleDraftStoreValue | null>(null);
const MemberGroupCreateDraftStoreContext = createContext<MemberGroupCreateDraftStoreValue | null>(null);

export function ScheduleDraftStoreProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [createDraft, setCreateDraftState] = useState<ScheduleDraftStoreValue['createDraft']>(null);
  const [editDrafts, setEditDrafts] = useState<ReadonlyMap<string, ScheduleDraftInput>>(() => new Map());
  const setCreateDraft = useCallback((selectedDate: string, draft: ScheduleDraftInput) => {
    setCreateDraftState({ selectedDate, draft });
  }, []);
  const clearCreateDraft = useCallback(() => {
    setCreateDraftState(null);
  }, []);
  const setEditDraft = useCallback((scheduleId: string, draft: ScheduleDraftInput) => {
    setEditDrafts((current) => {
      const next = new Map(current);
      next.set(scheduleId, draft);
      return next;
    });
  }, []);
  const clearEditDraft = useCallback((scheduleId: string) => {
    setEditDrafts((current) => {
      if (!current.has(scheduleId)) {
        return current;
      }
      const next = new Map(current);
      next.delete(scheduleId);
      return next;
    });
  }, []);
  const store = useMemo<ScheduleDraftStoreValue>(
    () => ({
      createDraft,
      editDrafts,
      setCreateDraft,
      clearCreateDraft,
      setEditDraft,
      clearEditDraft
    }),
    [clearCreateDraft, clearEditDraft, createDraft, editDrafts, setCreateDraft, setEditDraft]
  );

  return <ScheduleDraftStoreContext.Provider value={store}>{children}</ScheduleDraftStoreContext.Provider>;
}

export function MemberGroupCreateDraftStoreProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [name, setName] = useState('');
  const [initialMember, setInitialMember] = useState<MemberSelectorListItem | null>(null);
  const clearDraft = useCallback(() => {
    setName('');
    setInitialMember(null);
  }, []);
  const store = useMemo<MemberGroupCreateDraftStoreValue>(
    () => ({
      name,
      initialMember,
      setName,
      setInitialMember,
      clearDraft
    }),
    [clearDraft, initialMember, name]
  );

  return (
    <MemberGroupCreateDraftStoreContext.Provider value={store}>{children}</MemberGroupCreateDraftStoreContext.Provider>
  );
}

export function useScheduleDraftStore(): ScheduleDraftStoreValue {
  const store = useContext(ScheduleDraftStoreContext);
  if (!store) {
    throw new Error('Member React schedule draft store is missing');
  }
  return store;
}

export function useMemberGroupCreateDraftStore(): MemberGroupCreateDraftStoreValue {
  const store = useContext(MemberGroupCreateDraftStoreContext);
  if (!store) {
    throw new Error('Member React member group create draft store is missing');
  }
  return store;
}
